import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/shared/lib/cn';
import { useModuleRegistry } from '@/shared/registry/context';
import type { AttachmentSource } from '@/shared/registry/types';
import { Button } from '@/shared/ui/button';
import { SimpleTooltip } from '@/shared/ui/tooltip';

import type { FileUploadsState } from './useFileUploads';

/**
 * Buttons for the attachment sources modules register (`OrbitaModule.attachmentSources`, e.g. "Choose from Disk"),
 * each opening its picker; chosen files join the uploader's value like finished uploads. Renders nothing when no
 * module offers a source.
 */
export function AttachmentSourceButtons({
  uploads,
  compact = false,
  disabled,
  className,
}: {
  uploads: FileUploadsState;
  /** Icon-only buttons with a tooltip (composers). */
  compact?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const { attachmentSources } = useModuleRegistry();
  const [open, setOpen] = useState<AttachmentSource | null>(null);
  if (attachmentSources.length === 0) return null;
  const Picker = open?.picker;
  const off = disabled ?? uploads.disabled;
  return (
    <span className={cn('inline-flex items-center gap-1', className)} data-slot="attachment-sources">
      {attachmentSources.map((source) => {
        const Icon = source.icon;
        const label = t(source.labelKey);
        const button = (
          <Button
            key={source.id}
            type="button"
            variant="ghost"
            size={compact ? 'icon-sm' : 'sm'}
            disabled={off || uploads.remaining === 0}
            onClick={() => setOpen(source)}
            aria-label={compact ? label : undefined}
            data-attachment-source={source.id}
          >
            <Icon />
            {!compact && label}
          </Button>
        );
        return compact ? (
          <SimpleTooltip key={source.id} content={label}>
            {button}
          </SimpleTooltip>
        ) : (
          button
        );
      })}
      {Picker && (
        <Picker
          open
          onOpenChange={(value) => !value && setOpen(null)}
          onPick={(files) => {
            uploads.addFileRefs(files);
            setOpen(null);
          }}
          multiple={uploads.multiple}
          accept={uploads.accept}
          maxFiles={uploads.remaining}
        />
      )}
    </span>
  );
}
